import { OrbitControls, PerspectiveCamera } from '@react-three/drei'
import { MapGroundPlaneWithFallback } from 'features/kestrelLite/MapGroundPlane'
import { ReadOnly3DTilesAnnotations } from 'features/kestrelLite/ReadOnly3DTilesAnnotations'
import { TilesLayer } from 'features/kestrelLite/TilesLayer'
import { AxisConfig } from 'features/kestrelLite/utils/annotations'
import React, { FC, MutableRefObject } from 'react'
import { Vector3 } from 'three'
import { KVAnnotation } from 'types'

import { AutoFitCamera } from './AutoFitCamera'
import { AxesHelperComponent } from './AxesHelper'
import { FlyToTarget } from './FlyToTarget'
import { KeyboardFlyControls } from './KeyboardFlyControls'
import { MapStyle } from './MapStyleHotbar'
import { OrbitPivotSetter } from './OrbitPivotSetter'

interface LayerState {
  folder: string
  status: 'active' | 'loading' | 'fading-out'
  opacity: number
}

interface TilesSceneProps {
  kestrelModelId: string
  layers: LayerState[]
  showAxes: boolean
  showAnnotations: boolean
  showLabels: boolean
  showArea: boolean
  mapStyle: MapStyle
  origin?: { lat: number; long: number }
  annotations: KVAnnotation[]
  axisConfig: AxisConfig
  flyToTarget: Vector3 | null
  firstRendererRef: MutableRefObject<unknown>
  onLayerLoaded: (folder: string) => void
  onLayerRendererReady: (renderer: unknown, index: number) => void
  onFlyToComplete: () => void
  setControls: (controls: unknown) => void
  isActive?: boolean
  onMapLoadingChange?: (loading: boolean) => void
}

export const TilesScene: FC<TilesSceneProps> = ({
  kestrelModelId,
  layers,
  showAxes,
  showAnnotations,
  showLabels,
  showArea,
  mapStyle,
  origin,
  annotations,
  axisConfig,
  flyToTarget,
  firstRendererRef,
  onLayerLoaded,
  onLayerRendererReady,
  onFlyToComplete,
  setControls,
  isActive = false,
  onMapLoadingChange,
}) => {
  return (
    <>
      <PerspectiveCamera
        makeDefault
        fov={60}
        far={100000}
        near={0.1}
        position={[0, 50, 100]}
      />

      {showAxes && <AxesHelperComponent size={100} />}

      {layers.map((layer, index) => (
        <TilesLayer
          key={layer.folder}
          kestrelModelId={kestrelModelId}
          folder={layer.folder}
          visible={true}
          opacity={layer.opacity}
          onLoaded={() => onLayerLoaded(layer.folder)}
          onRendererReady={(renderer) => onLayerRendererReady(renderer, index)}
        />
      ))}

      <AutoFitCamera tilesRenderer={firstRendererRef.current} />

      {mapStyle !== 'off' && origin && (
        <MapGroundPlaneWithFallback
          lat={origin.lat}
          long={origin.long}
          metersPerUnit={1}
          mapStyle={mapStyle}
          onLoadingChange={onMapLoadingChange}
        />
      )}

      <ambientLight intensity={1.0} />
      <directionalLight position={[50, 100, 50]} intensity={1.5} />
      <directionalLight position={[-50, 50, -50]} intensity={0.5} />

      <OrbitControls
        makeDefault
        enableDamping
        dampingFactor={0.1}
        minDistance={0.1}
        maxDistance={10000}
        ref={(ref) => {
          if (ref) setControls(ref)
        }}
      />

      <OrbitPivotSetter />
      <KeyboardFlyControls speed={2} isActive={isActive} />

      {showAnnotations && (
        <ReadOnly3DTilesAnnotations
          annotations={annotations}
          axisConfig={axisConfig}
          showLabels={showLabels}
          showArea={showArea}
        />
      )}

      <FlyToTarget target={flyToTarget} onComplete={onFlyToComplete} />
    </>
  )
}
